import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const XIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const footerLinks = {
  product: [
    { href: 'https://app.churnlogix.com', label: 'Retention Builder', external: true },
    { href: '/how-it-works', label: 'How It Works' },
    { href: '/pricing', label: 'Pricing' },
  ],
  company: [
    { href: '/projects', label: 'Case Studies' },
    { href: '/newsletter', label: 'Newsletter' },
    { href: '/contact', label: 'Contact' },
  ],
  forOperators: [
    { href: '/how-it-works#signal-architecture', label: 'Signal Architecture' },
    { href: '/how-it-works#risk-categories', label: 'Risk Categories' },
    { href: '/how-it-works#intervention-playbooks', label: 'Intervention Playbooks' },
    { href: '/how-it-works#leading-metrics', label: 'Leading Metrics' },
    { href: '/how-it-works#renewal-process', label: 'Renewal Process' },
  ],
};

export function Footer() {
  const currentYear = new Date().getFullYear();
  const { actualTheme } = useTheme();

  return (
    <footer className="bg-slate-100 dark:bg-[#0A0C10] border-t border-slate-200 dark:border-slate-800">
      <div className="container-custom py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3">
              <img
                src={actualTheme === 'dark' ? '/logo-icon-dark.svg' : '/logo-icon-light.svg'}
                alt="ChurnLogix"
                className="w-10 h-10"
              />
              <span className="text-xl font-heading font-black">
                <span className="text-obsidian dark:text-signal">CHURN</span>
                <span className="text-slate-400 dark:text-slate-500">LOGIX</span>
              </span>
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-slate-600 dark:text-slate-500">
              We pinpoint why clients leave and build systems to stop it.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-4 mt-6">
              <a
                href="https://x.com/churnlogix"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded text-slate-500 hover:text-amber dark:text-slate-500 dark:hover:text-amber transition-colors"
                aria-label="X (Twitter)"
              >
                <XIcon className="w-5 h-5" />
              </a>
              <a
                href="mailto:hello@churnlogix.com"
                className="p-2 rounded text-slate-500 hover:text-amber dark:text-slate-500 dark:hover:text-amber transition-colors"
                aria-label="Email"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="font-heading font-black text-sm uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Product
            </h3>
            <ul className="mt-4 space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.label}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-slate-600 hover:text-amber dark:text-slate-500 dark:hover:text-amber transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-sm text-slate-600 hover:text-amber dark:text-slate-500 dark:hover:text-amber transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="font-heading font-black text-sm uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Company
            </h3>
            <ul className="mt-4 space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-600 hover:text-amber dark:text-slate-500 dark:hover:text-amber transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* For Operators */}
          <div>
            <h3 className="font-heading font-black text-sm uppercase tracking-wider text-slate-500 dark:text-slate-400">
              For Operators
            </h3>
            <ul className="mt-4 space-y-3">
              {footerLinks.forOperators.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-600 hover:text-amber dark:text-slate-500 dark:hover:text-amber transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              &copy; {currentYear} ChurnLogix. All rights reserved.
            </p>
            <p className="text-sm text-slate-500">
              Built for operators who treat retention as an engineering problem.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
